// B1319: Access — who may do what on which collection. Every rule is the
// server's answer to `SHOW GRANTS` (B347), and every change is a GRANT or a
// REVOKE of web/docs/sql.md § Users, roles and permissions, sent on the
// shared WS. After each change the page reloads `SHOW GRANTS`; it never
// patches its list, because the server's answer is the truth.
//
// Three views, each a route: `#/access/collection/<name>` (who has access to
// the collection), `#/access/role/<name>` and `#/access/user/<name>` (what
// the role or the user can reach). There is no `GRANT … TO USER`: a user gets
// rights through its roles, ANYONE, AUTHENTICATED and MATCH, so the user view
// adds a rule through one of the user's roles.
//
// The listings are superuser-only and read the CURRENT database. A non-root
// session sees the server's `permission denied`, never a fake empty list.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { I } from '../components/icons.jsx';
import * as grants from '../data/grants-store.js';
import * as users from '../data/users-store.js';
import { getCurrentDb, subscribeCurrentDb } from '../data/db-store.js';
import { accessHref, parseAccessSub } from '../data/access-route.js';

function errText(e) { return e?.message || String(e); }

// Every listing the page shows, loaded together. Each listing keeps its own
// error: a refused `SHOW GRANTS` must not hide the collections, and the
// reverse.
function useAccessData() {
  const [state, setState] = useState({
    rules: null, collections: null, roles: null, users: null, errors: {}, loading: true, db: getCurrentDb(),
  });
  const reload = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    const db = getCurrentDb();
    const [g, c, r, u] = await Promise.allSettled([
      grants.listGrants(), grants.listCollections(), users.listRoles(), users.listUsers(),
    ]);
    const val = (p) => (p.status === 'fulfilled' ? p.value : null);
    const err = (p) => (p.status === 'rejected' ? errText(p.reason) : null);
    setState({
      rules: val(g), collections: val(c), roles: val(r), users: val(u),
      errors: { rules: err(g), collections: err(c), roles: err(r), users: err(u) },
      loading: false, db,
    });
  }, []);
  useEffect(() => { reload(); }, [reload]);
  // The rules and the collections belong to the current database.
  useEffect(() => subscribeCurrentDb(() => { reload(); }), [reload]);
  return { ...state, reload };
}

// Run one server action, surface its error next to the control, then reload.
function useAction(reload) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const run = useCallback(async (fn) => {
    setBusy(true); setError(null);
    try { await fn(); await reload(); return true; }
    catch (e) { setError(errText(e)); return false; }
    finally { setBusy(false); }
  }, [reload]);
  return { busy, error, setError, run };
}

const VIEWS = [
  { id: 'collection', label: 'Collections' },
  { id: 'role', label: 'Roles' },
  { id: 'user', label: 'Users' },
];

function Link({ view, name, children }) {
  return <a className="access-link" href={accessHref(view, name)}>{children ?? name}</a>;
}

function RightsBoxes({ value, onChange, disabled }) {
  return (
    <span className="access-rights">
      {grants.PERMISSIONS.map((p) => {
        const on = value.includes(p);
        return (
          <label key={p} className={'users-check' + (on ? ' on' : '')}>
            <input type="checkbox" checked={on} disabled={disabled}
              onChange={() => onChange(on ? value.filter((x) => x !== p) : grants.PERMISSIONS.filter((x) => x === p || value.includes(x)))} />
            {p}
          </label>
        );
      })}
    </span>
  );
}

// The `grant` column as the page shows it: a role links to its view.
function WhoCell({ row }) {
  const t = row.target;
  if (t.kind === 'role') return <span>role <Link view="role" name={t.name} /></span>;
  if (t.kind === 'anyone') return <span>anyone</span>;
  if (t.kind === 'authenticated') return <span>every logged-in user</span>;
  if (t.kind === 'match') return <span>rows where <span className="mono">{t.field}</span> names the user</span>;
  return <span className="mono">{row.grant}</span>;
}

// ── one rule: the rights to edit, Save, Revoke ───────────────────────────
function RuleRow({ row, view, reload }) {
  const [rights, setRights] = useState(row.rights);
  const [confirm, setConfirm] = useState(false);
  const { busy, error, run } = useAction(reload);
  // A reload keeps an unsaved edit, unless the rights of this rule changed.
  const rightsKey = row.rights.join(',');
  useEffect(() => { setRights(row.rights); }, [rightsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const rev = grants.revokeFor(row);
  const editable = !!rev.sql;
  const changed = rights.length !== row.rights.length || rights.some((p) => !row.rights.includes(p));
  const shared = view === 'user' && row.via && !row.via.startsWith('role ');

  let pending = [];
  let pendingErr = null;
  if (editable && changed) {
    try { pending = grants.editSql(row, rights); } catch (e) { pendingErr = errText(e); }
  }

  return (
    <tr>
      {view !== 'collection' && <td className="fg1"><Link view="collection" name={row.collection} /></td>}
      {view === 'collection' && <td className="fg1"><WhoCell row={row} /></td>}
      <td>
        <RightsBoxes value={rights} onChange={setRights} disabled={busy || !editable} />
        {!editable && <div className="users-muted access-reason">Read-only: {rev.reason}</div>}
        {changed && pending.length > 0 && <div className="access-sql">{pending.join(';\n')}</div>}
        {pendingErr && <div className="users-error">{pendingErr}</div>}
        {error && <div className="users-error">{error}</div>}
      </td>
      <td className="mono access-where">{row.where ?? <span className="users-muted">—</span>}</td>
      {view === 'user' && (
        <td>
          {row.target.kind === 'role' ? <span>role <Link view="role" name={row.target.name} /></span> : row.via}
          {shared && <div className="users-muted">shared: a change reaches every user</div>}
        </td>
      )}
      <td className="access-actions">
        {editable && (
          <>
            <button className="btn btn-sm" type="button" disabled={busy || !changed || pending.length === 0}
              onClick={() => run(() => grants.runSql(grants.editSql(row, rights)))}>Save</button>
            {confirm ? (
              <>
                <button className="btn btn-sm users-danger" type="button" disabled={busy} title={rev.sql}
                  onClick={async () => { if (await run(() => grants.runSql([rev.sql]))) setConfirm(false); }}>Revoke</button>
                <button className="btn btn-ghost btn-sm" type="button" disabled={busy} onClick={() => setConfirm(false)}>Cancel</button>
              </>
            ) : (
              <button className="btn btn-ghost btn-sm" type="button" disabled={busy} title={rev.sql} onClick={() => setConfirm(true)}>Revoke…</button>
            )}
          </>
        )}
      </td>
    </tr>
  );
}

function RulesTable({ rows, view, reload }) {
  const heads = view === 'collection'
    ? ['Who', 'Rights', 'Where', '']
    : view === 'role'
      ? ['Collection', 'Rights', 'Where', '']
      : ['Collection', 'Rights', 'Where', 'Via', ''];
  return (
    <table className="k-table access-table">
      <thead><tr>{heads.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={heads.length} className="users-muted">No rules.</td></tr>
        )}
        {rows.map((r) => (
          <RuleRow key={`${r.collection}\u0000${r.grant}\u0000${r.where ?? ''}`} row={r} view={view} reload={reload} />
        ))}
      </tbody>
    </table>
  );
}

// ── the Add access form, preset to the view ──────────────────────────────
function AddForm({ view, name, collections, roles, userRoles, reload }) {
  const [kind, setKind] = useState('role');
  const [role, setRole] = useState('');
  const [field, setField] = useState('');
  const [collection, setCollection] = useState('');
  const [rights, setRights] = useState(['select']);
  const [useWhere, setUseWhere] = useState(false);
  const [wField, setWField] = useState('');
  const [wValue, setWValue] = useState('');
  const [wType, setWType] = useState('string');
  const { busy, error, setError, run } = useAction(reload);
  // A new selection is a new form.
  useEffect(() => { setRole(''); setCollection(''); setError(null); }, [view, name, setError]);

  let rule = null;
  if (view === 'collection') {
    const target = kind === 'role' ? { kind, name: role } : kind === 'match' ? { kind, field } : { kind };
    rule = { collection: name, target };
  } else if (view === 'role') {
    rule = { collection, target: { kind: 'role', name } };
  } else {
    rule = { collection, target: { kind: 'role', name: role } };
  }
  if (useWhere) rule.where = { field: wField, value: wValue, type: wType };
  rule.rights = rights;

  let sql = null;
  let composeErr = null;
  const ready = view === 'collection' ? true : !!collection;
  if (ready) {
    try { sql = grants.grantSql(rule); } catch (e) { composeErr = errText(e); }
  }

  async function submit(e) {
    e.preventDefault();
    if (!sql) return;
    if (await run(() => grants.runSql([sql]))) { setRights(['select']); setUseWhere(false); setWField(''); setWValue(''); }
  }

  if (view === 'user' && userRoles.length === 0) {
    return (
      <div className="users-muted">
        Rights reach a user through its roles. Add the user to a role on <a className="access-link" href="#/users">Users & roles</a> first.
      </div>
    );
  }

  return (
    <form className="access-add" onSubmit={submit}>
      <div className="users-form">
        {view === 'collection' && (
          <>
            <span className="users-label">To</span>
            <select value={kind} onChange={(e) => setKind(e.target.value)} disabled={busy}>
              <option value="role">ROLE</option>
              <option value="anyone">ANYONE</option>
              <option value="authenticated">AUTHENTICATED</option>
              <option value="match">MATCH (rows whose field names the user)</option>
            </select>
            {kind === 'role' && (
              <select value={role} onChange={(e) => setRole(e.target.value)} disabled={busy}>
                <option value="">{(roles ?? []).length ? 'pick a role…' : 'no roles'}</option>
                {(roles ?? []).map((r) => <option key={r.role} value={r.role}>{r.role}</option>)}
              </select>
            )}
            {kind === 'match' && (
              <input placeholder="field, e.g. owner" value={field} onChange={(e) => setField(e.target.value)} spellCheck={false} disabled={busy} />
            )}
          </>
        )}
        {view === 'user' && (
          <>
            <span className="users-label">Role</span>
            <select value={role} onChange={(e) => setRole(e.target.value)} disabled={busy}>
              <option value="">pick one of the user's roles…</option>
              {userRoles.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </>
        )}
        {view !== 'collection' && (
          <>
            <span className="users-label">Collection</span>
            <select value={collection} onChange={(e) => setCollection(e.target.value)} disabled={busy}>
              <option value="">pick a collection…</option>
              {(collections ?? []).map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
          </>
        )}
      </div>
      <div className="users-form">
        <span className="users-label">Rights</span>
        <RightsBoxes value={rights} onChange={setRights} disabled={busy} />
      </div>
      <div className="users-form">
        <label className={'users-check' + (useWhere ? ' on' : '')}>
          <input type="checkbox" checked={useWhere} onChange={() => setUseWhere(!useWhere)} disabled={busy} />
          only rows WHERE
        </label>
        {useWhere && (
          <>
            <input placeholder="field" value={wField} onChange={(e) => setWField(e.target.value)} spellCheck={false} disabled={busy} />
            <span>=</span>
            <input placeholder="value" value={wValue} onChange={(e) => setWValue(e.target.value)} spellCheck={false} disabled={busy} />
            <select value={wType} onChange={(e) => setWType(e.target.value)} disabled={busy}>
              <option value="string">string</option>
              <option value="integer">integer</option>
            </select>
          </>
        )}
      </div>
      {sql && <div className="access-sql">{sql}</div>}
      {composeErr && <div className="users-muted">{composeErr}</div>}
      <div className="users-form">
        <button className="btn btn-primary btn-sm" type="submit" disabled={busy || !sql}><I.Plus size={12} /> Grant</button>
        {error && <span className="users-error">{error}</span>}
      </div>
    </form>
  );
}

// ── the right side: the rules of the selected item ───────────────────────
function Detail({ view, name, data }) {
  const all = data.rules ?? [];
  const user = view === 'user' ? (data.users ?? []).find((u) => u.user === name) : null;
  const rows = view === 'collection'
    ? all.filter((r) => r.collection === name)
    : view === 'role'
      ? grants.rulesOfRole(all, name)
      : grants.rulesOfUser(all, { user: name, roles: user?.roles ?? [] });

  let nameErr = null;
  if (view === 'collection') {
    try { grants.quoteName(name); } catch (e) { nameErr = errText(e); }
  }

  const title = view === 'collection' ? `Collection ${name}` : view === 'role' ? `Role ${name}` : `User ${name}`;
  return (
    <div className="k-card">
      <div className="k-card-head">
        <span className="title">{title}</span>
        <div className="actions">
          {view === 'user' && user && (
            <span className="users-muted">
              roles: {user.roles.length === 0 ? '—' : user.roles.map((r, i) => (
                <span key={r}>{i ? ', ' : ''}<Link view="role" name={r} /></span>
              ))}
            </span>
          )}
        </div>
      </div>
      {view === 'user' && name === 'root' && (
        <div className="users-muted access-note">root is the superuser and passes every check. The rules below do not limit it.</div>
      )}
      {nameErr && <div className="users-error access-note">{nameErr}</div>}
      {data.errors.rules
        ? <div className="users-error access-note">{data.errors.rules}</div>
        : data.rules == null
          ? <div className="users-muted access-note">Loading…</div>
          : <RulesTable rows={rows} view={view} reload={data.reload} />}
      {data.rules != null && (
        <div className="access-note">
          <div className="users-label" style={{ marginBottom: 8 }}>Add access</div>
          <AddForm view={view} name={name} collections={data.collections} roles={data.roles}
            userRoles={user?.roles ?? []} reload={data.reload} />
        </div>
      )}
    </div>
  );
}

// ── the page ─────────────────────────────────────────────────────────────
export function AccessPage({ sub }) {
  const { view, name } = parseAccessSub(sub);
  const data = useAccessData();
  const [filter, setFilter] = useState('');

  const items = useMemo(() => {
    const rules = data.rules ?? [];
    if (view === 'collection') {
      const list = (data.collections ?? []).map((c) => ({ name: c.name, meta: c.type }));
      // A rule can name a collection that the listing does not hold.
      for (const r of rules) if (!list.some((c) => c.name === r.collection)) list.push({ name: r.collection, meta: '?' });
      return list.map((c) => ({ ...c, count: rules.filter((r) => r.collection === c.name).length }));
    }
    if (view === 'role') {
      return (data.roles ?? []).map((r) => ({ name: r.role, meta: `${r.members.length} member${r.members.length === 1 ? '' : 's'}`, count: grants.rulesOfRole(rules, r.role).length }));
    }
    return (data.users ?? []).map((u) => ({
      name: u.user,
      meta: u.user === 'root' ? 'superuser' : u.roles.join(', '),
      count: grants.rulesOfUser(rules, { user: u.user, roles: u.roles }).length,
    }));
  }, [view, data.collections, data.roles, data.users, data.rules]);

  const listError = view === 'collection' ? data.errors.collections : view === 'role' ? data.errors.roles : data.errors.users;
  const listLoaded = view === 'collection' ? data.collections : view === 'role' ? data.roles : data.users;
  const q = filter.trim().toLowerCase();
  const shown = q ? items.filter((it) => it.name.toLowerCase().includes(q)) : items;
  const noun = VIEWS.find((v) => v.id === view).label.toLowerCase();

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="crumbs">Admin · Access</div>
          <h1>Access</h1>
        </div>
        <div className="actions">
          <span className="users-muted">rules of database <span className="mono">{data.db}</span></span>
          <button className="btn btn-sm btn-ghost" type="button" onClick={data.reload} disabled={data.loading} title="Reload"><I.Refresh size={12} /> Refresh</button>
        </div>
      </div>
      <div className="page-tabs">
        {VIEWS.map((v) => (
          <a key={v.id} className={'tab' + (view === v.id ? ' active' : '')} href={accessHref(v.id)}>{v.label}</a>
        ))}
      </div>
      <div className="access-grid">
        <div className="k-card">
          <div className="k-card-head">
            <span className="title">{listLoaded == null ? VIEWS.find((v) => v.id === view).label : `${items.length} ${noun}`}</span>
            <div className="actions">
              <div className="users-filter">
                <I.Search size={12} />
                <input placeholder="Filter…" value={filter} onChange={(e) => setFilter(e.target.value)} />
              </div>
            </div>
          </div>
          {listError && <div className="users-error access-note">{listError}</div>}
          <div className="access-list">
            {listLoaded == null && !listError && <div className="users-muted access-note">Loading…</div>}
            {listLoaded != null && shown.length === 0 && (
              <div className="users-muted access-note">{q ? 'Nothing matches the filter.' : `No ${noun}.`}</div>
            )}
            {shown.map((it) => (
              <a key={it.name} href={accessHref(view, it.name)} className={'access-item' + (it.name === name ? ' active' : '')}>
                <span className="access-item-name">{it.name}</span>
                {it.meta && <span className="access-item-meta">{it.meta}</span>}
                <span className="users-role-count" title="rules">{data.rules == null ? '' : it.count}</span>
              </a>
            ))}
          </div>
        </div>
        {name
          ? <Detail view={view} name={name} data={data} />
          : (
            <div className="k-card">
              <div className="users-muted access-note">
                {view === 'collection' && 'Pick a collection to see who has access to it.'}
                {view === 'role' && 'Pick a role to see the collections it can reach.'}
                {view === 'user' && 'Pick a user to see the collections it can reach, and through which rule.'}
              </div>
            </div>
          )}
      </div>
    </div>
  );
}
